// src/components/payments/ManageSubscriptionActions.jsx
import React, { useEffect, useState } from "react";
import { onSnapshot, doc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase/firebaseServices";
import { useAuth } from "../../firebase/AuthContext";
import "bootstrap/dist/css/bootstrap.min.css";
import "./paymentsCss/ManageSubscriptionActions.css";
import AlertModal from "./server/alertModal";

import {
  FaHouse,
  FaCircleCheck,
  FaTriangleExclamation,
  FaArrowUpRightFromSquare,
  FaGaugeHigh,
  FaCalculator,
  FaBookOpen,
  FaCalendarDays,
} from "react-icons/fa6";

import {
  updateUserSubscriptionStatusById,
  clearNextBillingFieldsIfPastDueById,
} from "./PayPalSubscriptionService";
import { EntitlementCalculator } from "./RequireEntitlement";

const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/+$/, "");
const SERVICE = "phn.ui.billing";

/* Pretty money */
const toMoney = (value, currency = "USD") => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return "—";
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(numericValue);
  } catch {
    return `$${numericValue.toFixed(2)}`;
  }
};

/* Safe local time from ISO-like / Date / Timestamp */
const toLocal = (value) => {
  if (!value) return "—";
  let date;
  if (value instanceof Date) {
    date = value;
  } else if (value instanceof Timestamp) {
    date = value.toDate();
  } else if (typeof value === "string" || typeof value === "number") {
    date = new Date(value);
  } else if (value?.toDate) {
    date = value.toDate();
  } else {
    return "—";
  }
  return Number.isNaN(date.getTime()) ? "—" : date.toLocaleString();
};

/* Normalize subscription date field */
const asDateOrNullForSubField = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value;
  if (value instanceof Timestamp) return value.toDate();
  if (typeof value?.toDate === "function") return value.toDate();
  if (typeof value === "string" || typeof value === "number") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
};

/* Event envelope (guardrails-compatible) */
const djb2 = (stringValue) => {
  let hash = 5381;
  for (let index = 0; index < stringValue.length; index++) {
    hash = ((hash << 5) + hash) ^ stringValue.charCodeAt(index);
  }
  return (hash >>> 0).toString(16);
};
const randomHex = () =>
  Array.from(crypto.getRandomValues(new Uint8Array(16)), (byte) =>
    byte.toString(16).padStart(2, "0")
  ).join("");
const buildEvent = (payload) => {
  const requestId = randomHex();
  return {
    source: SERVICE,
    requestId,
    traceId: requestId,
    visited: [],
    hopCount: 0,
    publishedAt: new Date().toISOString(),
    idempotencyKey: `${SERVICE}:${requestId}`,
    changeHash: djb2(JSON.stringify(payload)),
  };
};

/* HTTP with retries + 204 normalize */
async function fetchJsonRetry(url, options = {}, tries = 3, baseDelay = 400) {
  let lastError;
  for (let attempt = 0; attempt < tries; attempt++) {
    try {
      const response = await fetch(url, options);
      if (response.status === 204) return { ok: true, status: 204 };
      const text = await response.text();
      let json = null;
      try {
        json = text ? JSON.parse(text) : null;
      } catch {}
      if (!response.ok)
        throw new Error(json?.error || `HTTP ${response.status}`);
      if (!json || typeof json.ok === "undefined")
        return { ...(json || {}), ok: true };
      return json;
    } catch (error) {
      lastError = error;
      const delay =
        baseDelay * Math.pow(2, attempt) + Math.floor(Math.random() * 150);
      await new Promise((resolve) => setTimeout(resolve, delay));
    }
  }
  throw lastError;
}

/* Tiny UI badges */
function StatusBadge({ status }) {
  const normalizedStatus = (status || "").toUpperCase();
  const classMap = {
    ACTIVE: "bg-success-soft text-success fw-semibold",
    SUSPENDED: "bg-warning-soft text-warning fw-semibold",
    CANCELLED: "bg-danger-soft text-danger fw-semibold",
  };
  const className =
    "msa-badge rounded-pill px-3 py-2 text-uppercase " +
    (classMap[normalizedStatus] ||
      "bg-secondary-soft text-secondary fw-semibold");
  return <span className={className}>{normalizedStatus || "—"}</span>;
}

function TrialBadge({ active }) {
  const className = active
    ? "msa-badge rounded-pill px-3 py-2 text-uppercase bg-success-soft text-success fw-semibold"
    : "msa-badge rounded-pill px-3 py-2 text-uppercase bg-secondary-soft text-secondary fw-semibold";
  return (
    <span className={className}>{active ? "TRIAL ACTIVE" : "TRIAL ENDED"}</span>
  );
}

export default function ManageSubscriptionActions() {
  const { currentUser } = useAuth();

  // Primary subscription (the one UI actions operate on by default)
  const [subId, setSubId] = useState(null);
  const [status, setStatus] = useState(null);
  const [planName, setPlanName] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [amount, setAmount] = useState(null);
  const [currency, setCurrency] = useState("USD");
  const [nextBill, setNextBill] = useState(null);

  // All subscriptions (for multi-plan displays)
  const [allSubscriptions, setAllSubscriptions] = useState([]);

  // Full user snapshot (for entitlement calculator)
  const [userSnapshot, setUserSnapshot] = useState(null);

  // Trial state
  const [trialStart, setTrialStart] = useState(null);
  const [trialEnd, setTrialEnd] = useState(null);
  const [trialActive, setTrialActive] = useState(false);

  // UI state
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [banner, setBanner] = useState(null);

  // Confirmation modal state (now includes target subscription info)
  const [confirmState, setConfirmState] = useState({
    open: false,
    action: null, // 'suspend' | 'cancel'
    subscriptionId: null,
    planName: null,
    planId: null,
    nextBillingIso: null,
  });

  const openConfirm = (action, subscription) => {
    const chosenSubscription = subscription || null;

    const subscriptionId =
      chosenSubscription?.subscriptionId || subId || null;

    const computedPlanName =
      chosenSubscription?.planName ||
      chosenSubscription?.planId ||
      planName ||
      planId ||
      null;

    const computedPlanId = chosenSubscription?.planId || planId || null;

    const nextBillingDate =
      asDateOrNullForSubField(chosenSubscription?.nextBillingTime) ||
      (nextBill ? new Date(nextBill) : null);

    setConfirmState({
      open: true,
      action,
      subscriptionId,
      planName: computedPlanName,
      planId: computedPlanId,
      nextBillingIso: nextBillingDate ? nextBillingDate.toISOString() : null,
    });
  };

  const closeConfirm = () =>
    setConfirmState({
      open: false,
      action: null,
      subscriptionId: null,
      planName: null,
      planId: null,
      nextBillingIso: null,
    });

  useEffect(() => {
    if (!currentUser?.uid) return;
    const userReference = doc(db, "users", currentUser.uid);

    const unsubscribe = onSnapshot(userReference, (snapshot) => {
      const userData = snapshot.data() || {};
      setUserSnapshot(userData);

      const subscriptions = Array.isArray(userData?.subscriptions)
        ? userData.subscriptions
        : [];
      setAllSubscriptions(subscriptions);

      // Primary subscription = active one, or most recent (index 0)
      const activeSubscription =
        subscriptions.find((subscription) => subscription?.isActive) ||
        subscriptions[0] ||
        null;

      const activeId = activeSubscription?.subscriptionId || null;
      setSubId(activeId);
      setStatus(activeSubscription?.status || null);
      setPlanName(activeSubscription?.planName || null);
      setPlanId(activeSubscription?.planId || null);

      const nextBillingDate =
        activeSubscription?.nextBillingTime?.toDate?.() ??
        (activeSubscription?.nextBillingTime instanceof Date
          ? activeSubscription.nextBillingTime
          : null);
      setNextBill(nextBillingDate ? nextBillingDate.toISOString() : null);

      setAmount(activeSubscription?.lastPayment?.amount ?? null);
      setCurrency(activeSubscription?.lastPayment?.currency || "USD");

      const startTrial =
        userData?.startTrialPeriodDate?.toDate?.() ||
        (userData?.startTrialPeriodDate instanceof Date
          ? userData.startTrialPeriodDate
          : null);
      const endTrial =
        userData?.endTrialPeriodDate?.toDate?.() ||
        (userData?.endTrialPeriodDate instanceof Date
          ? userData.endTrialPeriodDate
          : null);
      const rootTrialFlag = userData?.isTrialPeriodActive === true;

      setTrialStart(startTrial || null);
      setTrialEnd(endTrial || null);
      setTrialActive(Boolean(rootTrialFlag && endTrial && endTrial > new Date()));
    });

    return () => unsubscribe();
  }, [currentUser?.uid]);

  useEffect(() => {
    let isAlive = true;
    (async () => {
      if (!subId) {
        setLoading(false);
        return;
      }
      try {
        const json = await fetchJsonRetry(
          `${API_BASE}/api/paypal/subscriptions/${encodeURIComponent(subId)}`,
          {
            headers: { Accept: "application/json" },
          },
          3
        );
        if (!isAlive) return;
        setStatus(json?.status || null);
        setPlanId(json?.plan_id || json?.planId || planId);
        setPlanName(json?.plan_name || json?.planName || planName);
        setAmount(json?.price ?? json?.last_payment_amount?.value ?? amount);
        setCurrency(json?.currency_code || currency);
        setNextBill(json?.next_billing_time || nextBill);
      } catch (error) {
        setBanner({
          kind: "error",
          title: "Couldn’t refresh subscription",
          body: error.message || String(error),
        });
      } finally {
        if (isAlive) setLoading(false);
      }
    })();
    return () => {
      isAlive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subId]);

  // Passive cleanup for past-due nextBill when suspended/cancelled (primary)
  useEffect(() => {
    if (!currentUser?.uid) return;
    const normalizedStatus = String(status || "").toUpperCase();
    if (normalizedStatus !== "SUSPENDED" && normalizedStatus !== "CANCELLED")
      return;
    if (!nextBill) return;
    const nextBillingDate = new Date(nextBill);
    if (Number.isNaN(nextBillingDate.getTime())) return;
    if (nextBillingDate.getTime() > Date.now()) return;
    if (!subId) return;
    clearNextBillingFieldsIfPastDueById(db, currentUser.uid, subId).catch(
      () => {}
    );
  }, [status, nextBill, currentUser?.uid, subId]);

  const canPause = status === "ACTIVE";
  const canResume = status === "SUSPENDED";
  const canCancelPrimary = status === "ACTIVE" || status === "SUSPENDED";

  /**
   * Perform server-side action (activate/suspend/cancel) on a specific subscription.
   * If targetSubscriptionId is omitted, the primary subId is used.
   */
  async function doAction(action, targetSubscriptionId) {
    const targetId = targetSubscriptionId || subId;
    if (!targetId || !currentUser?.uid) return;

    setBusy(true);
    setBanner(null);

    try {
      const payload = {
        reason: `user_${action}`,
        __event: buildEvent({ subId: targetId, action }),
      };

      await fetchJsonRetry(
        `${API_BASE}/api/paypal/subscriptions/${encodeURIComponent(
          targetId
        )}/${action}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify(payload),
        },
        3
      );

      const newStatus =
        action === "cancel"
          ? "CANCELLED"
          : action === "suspend"
          ? "SUSPENDED"
          : "ACTIVE";

      // Only update primary status state if we acted on the primary subscription.
      if (targetId === subId) {
        setStatus(newStatus);
      }

      await updateUserSubscriptionStatusById(
        db,
        currentUser.uid,
        targetId,
        newStatus
      );
      await clearNextBillingFieldsIfPastDueById(
        db,
        currentUser.uid,
        targetId
      );

      const title =
        action === "cancel"
          ? "Subscription Canceled"
          : action === "suspend"
          ? "Subscription Paused"
          : "Subscription Resumed";
      const body =
        action === "cancel"
          ? "Future renewals have been stopped."
          : action === "suspend"
          ? "You won’t be charged while suspended."
          : "Your plan is active again.";
      setBanner({ kind: "success", title, body });
    } catch (error) {
      setBanner({
        kind: "error",
        title: "Action failed",
        body: error.message || String(error),
      });
    } finally {
      setBusy(false);
    }
  }

  const paypalManageHref =
    (process.env.REACT_APP_PAYPAL_ENV || "live").toLowerCase() === "sandbox"
      ? "https://www.sandbox.paypal.com/myaccount/autopay/"
      : "https://www.paypal.com/myaccount/autopay/";

  // Effective coverage (trial + subs + stacking + grace) using the shared calculator
  const entitlementSnapshot = EntitlementCalculator.fromUserDocument(
    userSnapshot || {},
    new Date()
  );
  const effectivePaidUntil = entitlementSnapshot.until;
  const hasDashboardAccess = entitlementSnapshot.entitled;
  const headerDest = hasDashboardAccess ? "/my-nutrition-status" : "/home";

  const Header = () => (
    <div className="mb-4">
      <div className="d-flex align-items-center justify-content-between gap-3">
        <h2 className="fw-bold fs-3 mb-0">Your Pure Health Nexus Plan</h2>
        <a
          className="btn btn-light rounded-circle shadow-sm p-2"
          href={headerDest}
          aria-label="Home"
        >
          <FaHouse />
        </a>
      </div>
      <p className="text-muted mb-0 mt-1">
        You’re on the right track to a healthier you.
      </p>
    </div>
  );

  const nowMilliseconds = Date.now();
  const daysLeft = trialEnd
    ? Math.max(
        0,
        Math.ceil(
          (trialEnd.getTime() - nowMilliseconds) / (24 * 60 * 60 * 1000)
        )
      )
    : null;
  const totalDays =
    trialStart && trialEnd
      ? Math.max(
          0,
          Math.round(
            (trialEnd.getTime() - trialStart.getTime()) /
              (24 * 60 * 60 * 1000)
          )
        )
      : null;

  // Loading skeleton
  if (loading) {
    return (
      <main className="msa-root container py-4">
        <div className="mx-auto" style={{ maxWidth: "700px" }}>
          <Header />
          <section className="card border-0 shadow-sm">
            <div className="card-body">Loading your subscription…</div>
          </section>
        </div>
      </main>
    );
  }

  // No subscription — leave as-is (no modal needed here)
  if (!subId) {
    const hasTrial = !!(trialStart && trialEnd);
    return (
      <main className="msa-root container py-4">
        <div className="mx-auto" style={{ maxWidth: "700px" }}>
          <Header />
          <section className="card border-0 shadow-sm mb-3">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="fw-bold mb-0">Free Trial</h5>
                <TrialBadge active={trialActive} />
              </div>

              {hasTrial ? (
                <>
                  <div className="d-flex align-items-center gap-3 flex-wrap mb-2">
                    <div className="countdown-lg">
                      {trialActive
                        ? `${daysLeft} day${
                            daysLeft === 1 ? "" : "s"
                          } left`
                        : "0 days left"}
                    </div>
                    {trialActive && (
                      <div className="text-muted">
                        <FaCalendarDays className="me-1" />
                        Ends {toLocal(trialEnd)}
                      </div>
                    )}
                  </div>

                  <dl className="row mb-0">
                    <dt className="col-6 text-muted">Started</dt>
                    <dd className="col-6 text-end fw-semibold">
                      {toLocal(trialStart)}
                    </dd>

                    <dt className="col-6 text-muted">Ends</dt>
                    <dd className="col-6 text-end fw-semibold">
                      {toLocal(trialEnd)}
                    </dd>

                    {typeof totalDays === "number" && (
                      <>
                        <dt className="col-6 text-muted">Total Length</dt>
                        <dd className="col-6 text-end fw-semibold">
                          {totalDays} days
                        </dd>
                      </>
                    )}
                  </dl>
                </>
              ) : (
                <p className="text-muted mb-0">
                  You don’t have an active subscription yet. Start your free
                  trial to explore the dashboard and tools.
                </p>
              )}
            </div>
          </section>

          <div className="d-grid gap-2 mb-4">
            {trialActive && (
              <>
                <a
                  href="/my-nutrition-status"
                  className="btn msa-btn-success fw-bold py-2 text-center"
                >
                  Continue Free Trial
                </a>
                <a
                  href="/home"
                  className="btn btn-outline-secondary fw-bold py-2 text-center"
                >
                  Upgrade Now
                </a>
              </>
            )}
            {!trialActive && hasTrial && (
              <a
                href="/home"
                className="btn msa-btn-success fw-bold py-2 text-center"
              >
                Trial Ended — Upgrade to Keep Going
              </a>
            )}
            {!hasTrial && (
              <a
                href="/my-nutrition-status"
                className="btn msa-btn-success fw-bold py-2 text-center"
              >
                Start Free Trial
              </a>
            )}
          </div>

          <section className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="fw-bold mb-2">No Active Subscription</h5>
              <p className="text-muted mb-0">
                Pick a plan on the{" "}
                <a className="link-green fw-semibold" href="/home">
                  Pricing
                </a>{" "}
                page. You can manage billing in PayPal once a plan is active.
              </p>
            </div>
          </section>
        </div>
      </main>
    );
  }

  // Subscribed — show dashboard + all plans + action buttons + confirmation modal
  const confirmPlanLabel =
    confirmState.planName || confirmState.planId || "—";
  const confirmNextBillingLabel = toLocal(confirmState.nextBillingIso);

  const confirmContentByAction = {
    suspend: {
      title: "Pause Subscription",
      message: (
        <>
          <p>
            Pausing stops renewals until you resume. You won’t be charged while
            paused.
          </p>
          <ul className="mb-0">
            <li>
              <strong>Plan:</strong> {confirmPlanLabel}
            </li>
            <li>
              <strong>Next Billing:</strong> {confirmNextBillingLabel}
            </li>
          </ul>
        </>
      ),
      variant: "warning",
      primaryLabel: "Continue",
      secondaryLabel: "Cancel",
    },
    cancel: {
      title: "Cancel Subscription",
      message: (
        <>
          <p>
            Cancellation ends future renewals. You’ll keep access until the
            current period ends.
          </p>
          <ul className="mb-0">
            <li>
              <strong>Plan:</strong> {confirmPlanLabel}
            </li>
            <li>
              <strong>Next Billing:</strong> {confirmNextBillingLabel}
            </li>
          </ul>
        </>
      ),
      variant: "error",
      primaryLabel: "Continue",
      secondaryLabel: "Cancel",
    },
  };

  return (
    <main className="msa-root container py-4">
      <div className="mx-auto" style={{ maxWidth: "700px" }}>
        <Header />

        <div className="vstack gap-4">
          {/* Effective coverage summary (trial + paid + stacking + grace) */}
          <section className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="fw-bold mb-2">Access Summary</h5>
              {effectivePaidUntil ? (
                <p className="mb-0">
                  You currently have full access through{" "}
                  <strong>{toLocal(effectivePaidUntil)}</strong>.
                </p>
              ) : (
                <p className="mb-0">
                  Your access has no fixed end date yet — your subscriptions
                  renew automatically until you change them.
                </p>
              )}
            </div>
          </section>

          {/* Primary plan snapshot */}
          <section className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="fw-bold mb-3">At-a-Glance Dashboard</h5>
              <dl className="row mb-0">
                <dt className="col-6 text-muted">Primary Plan</dt>
                <dd className="col-6 text-end fw-semibold">
                  {planName || planId || "—"}
                </dd>

                <dt className="col-6 text-muted">Next Billing Date</dt>
                <dd className="col-6 text-end fw-semibold">
                  {toLocal(nextBill)}
                </dd>

                <dt className="col-6 text-muted">Status</dt>
                <dd className="col-6 text-end">
                  <StatusBadge status={status} />
                </dd>
              </dl>
            </div>
          </section>

          {/* All plans list (Flex + Committed, etc.) */}
          <section className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="fw-bold mb-3">All Plans on Your Account</h5>
              {allSubscriptions.length === 0 && (
                <p className="text-muted mb-0">
                  We could not find any subscriptions on your account.
                </p>
              )}

              {allSubscriptions.length > 0 && (
                <div className="vstack gap-3">
                  {allSubscriptions.map((subscription, index) => {
                    const thisStatus =
                      subscription?.status ||
                      subscription?.activeSubscriptionStatus ||
                      "UNKNOWN";
                    const statusUpper = String(thisStatus).toUpperCase();
                    const thisIsPrimary = subscription?.isActive;
                    const thisStart = asDateOrNullForSubField(
                      subscription?.startTime
                    );
                    const thisNext = asDateOrNullForSubField(
                      subscription?.nextBillingTime
                    );

                    const canCancelThis =
                      statusUpper === "ACTIVE" || statusUpper === "SUSPENDED";

                    return (
                      <div
                        key={subscription.subscriptionId || `sub-${index}`}
                        className="border rounded-3 p-3 bg-light-subtle"
                      >
                        <div className="d-flex justify-content-between align-items-center mb-1">
                          <div>
                            <div className="fw-bold">
                              {subscription.planName ||
                                subscription.planId ||
                                "Unnamed plan"}
                            </div>
                            <div className="text-muted small">
                              Subscription ID:{" "}
                              <span className="font-monospace">
                                {subscription.subscriptionId || "—"}
                              </span>
                            </div>
                          </div>
                          <div className="text-end">
                            <StatusBadge status={thisStatus} />
                            {thisIsPrimary && (
                              <div className="small text-success fw-semibold mt-1">
                                Primary plan
                              </div>
                            )}
                          </div>
                        </div>

                        <dl className="row mb-2 small">
                          <dt className="col-6 text-muted">Started</dt>
                          <dd className="col-6 text-end fw-semibold">
                            {toLocal(thisStart)}
                          </dd>

                          <dt className="col-6 text-muted">Next Billing</dt>
                          <dd className="col-6 text-end fw-semibold">
                            {toLocal(thisNext)}
                          </dd>

                          <dt className="col-6 text-muted">Renewal Amount</dt>
                          <dd className="col-6 text-end fw-semibold">
                            {toMoney(
                              subscription?.lastPayment?.amount,
                              subscription?.lastPayment?.currency || "USD"
                            )}
                          </dd>
                        </dl>

                        {canCancelThis && (
                          <div className="text-end">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-danger fw-semibold"
                              disabled={busy}
                              onClick={() => openConfirm("cancel", subscription)}
                            >
                              Cancel this plan
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </section>

          {/* Billing details for the primary plan */}
          <section className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Billing Details</h5>
              <dl className="row mb-0">
                <dt className="col-6 text-muted">Renewal Amount</dt>
                <dd className="col-6 text-end fw-semibold">
                  {toMoney(amount, currency)}
                </dd>

                <dt className="col-6 text-muted">Payment Method</dt>
                <dd className="col-6 text-end">
                  <a
                    href={paypalManageHref}
                    target="_blank"
                    rel="noreferrer"
                    className="link-green fw-semibold"
                  >
                    PayPal
                  </a>
                </dd>
              </dl>
            </div>
          </section>

          {/* Health dashboard shortcuts */}
          <section className="card border-0 shadow-sm">
            <div className="card-body">
              <h5 className="fw-bold mb-3">Health Dashboard</h5>
              <div className="row g-3 text-center">
                <div className="col-12 col-md-4">
                  <a
                    href="/my-nutrition-status"
                    className="tile d-flex flex-column justify-content-center align-items-center text-decoration-none"
                  >
                    <FaGaugeHigh className="tile-icon" />
                    <div className="tile-title">View Dashboard</div>
                  </a>
                </div>
                <div className="col-12 col-md-4">
                  <div className="tile">
                    <FaCalculator className="tile-icon" />
                    <div className="tile-number">15+</div>
                    <div className="tile-sub">Health Calculators</div>
                  </div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="tile">
                    <FaBookOpen className="tile-icon" />
                    <div className="tile-number">50+</div>
                    <div className="tile-sub">Resource Library</div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {banner && (
            <div
              className={`alert d-flex align-items-start gap-2 ${
                banner.kind === "success"
                  ? "alert-success msa-alert-success"
                  : "alert-danger msa-alert-danger"
              } rounded-3`}
              role="alert"
            >
              {banner.kind === "success" ? (
                <FaCircleCheck className="fs-5 mt-1" />
              ) : (
                <FaTriangleExclamation className="fs-5 mt-1" />
              )}
              <div>
                <strong>{banner.title}</strong>
                <div>{banner.body}</div>
              </div>
            </div>
          )}

          {/* Primary-level controls (still operate on primary plan) */}
          <div className="d-grid gap-3">
            {canResume && (
              <button
                className="btn btn-success msa-btn-success fw-bold py-2"
                disabled={busy}
                onClick={() => doAction("activate")}
              >
                Resume My Journey
              </button>
            )}

            {canPause && (
              <button
                className="btn btn-outline-secondary fw-bold py-2"
                disabled={busy}
                onClick={() => openConfirm("suspend")}
              >
                Pause Subscription
              </button>
            )}

            {canCancelPrimary && (
              <button
                className="btn btn-outline-danger fw-bold py-2"
                disabled={busy}
                onClick={() => openConfirm("cancel")}
              >
                Cancel Primary Subscription
              </button>
            )}
          </div>

          <div className="text-center pt-2">
            <a
              className="text-muted text-decoration-underline small"
              href={paypalManageHref}
              target="_blank"
              rel="noreferrer"
            >
              For detailed billing history, manage in PayPal{" "}
              <FaArrowUpRightFromSquare size={12} />
            </a>
          </div>
        </div>
      </div>

      {/* Confirmation modal */}
      {confirmState.open && (
        <AlertModal
          open
          title={confirmContentByAction[confirmState.action]?.title}
          message={confirmContentByAction[confirmState.action]?.message}
          variant={confirmContentByAction[confirmState.action]?.variant}
          requester={{
            entityId: `${
              currentUser?.uid || "anonymous"
            }:${confirmState.subscriptionId || "no-sub"}`,
          }}
          primaryAction={{
            label: confirmContentByAction[confirmState.action]?.primaryLabel,
            onClick: async () => {
              await doAction(confirmState.action, confirmState.subscriptionId);
              closeConfirm();
            },
          }}
          secondaryAction={{
            label: confirmContentByAction[confirmState.action]?.secondaryLabel,
            onClick: () => closeConfirm(),
          }}
          onClose={closeConfirm}
          service="phn.ui.billing.alert"
        />
      )}
    </main>
  );
}
